import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, HandHeart, LayoutDashboard, SquarePlus, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { APP_NAME, sectionBar } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { AppMenu } from "@/components/app-menu";
import { AppSidebar } from "@/components/app-sidebar";
import { ScopeSwitcher } from "@/components/scope-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { UserMenu } from "@/components/user-menu";
import { Button } from "@/components/ui/button";

/**
 * Responsive frame.
 * - Phone (< md): single column filling the small viewport height; app bar on top with the
 *   hamburger drawer, bottom tab bar, only the content scrolls.
 * - Desktop (md+): persistent left sidebar, app bar across the content, no bottom bar.
 *   Pages decide their own width inside <main> (see PageContainer in page.tsx).
 */
export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const bar = sectionBar(pathname);

  return (
    <div className="flex h-svh w-full bg-background">
      <AppSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="pt-safe sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
          <div className="flex h-14 items-center gap-1 px-2 md:px-4">
            {bar ? (
              <>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Back"
                  onClick={() => navigate(bar.back)}
                >
                  <ChevronLeft className="size-6" />
                </Button>
                <h1 className="min-w-0 truncate text-base font-semibold">{bar.title}</h1>
              </>
            ) : (
              <>
                <div className="md:hidden">
                  <AppMenu />
                </div>
                <h1 className="min-w-0 truncate pl-1 text-base font-semibold md:pl-0">
                  <span className="md:hidden">{APP_NAME}</span>
                  <span className="hidden md:inline">Dashboard</span>
                </h1>
              </>
            )}
            <div className="ml-auto flex min-w-0 items-center gap-1">
              <ScopeSwitcher />
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>

        <BottomNav />
      </div>
    </div>
  );
}

interface TabItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  prominent?: boolean;
}

const TAB_ITEMS: TabItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/donations", label: "Donations", icon: HandHeart, end: true },
  { to: "/donations/new", label: "New", icon: SquarePlus, end: false, prominent: true },
  { to: "/donors", label: "Donors", icon: Users, end: false },
];

function BottomNav() {
  return (
    <nav className="pb-safe border-t border-border bg-card/95 backdrop-blur md:hidden" aria-label="Primary">
      <ul className="grid grid-cols-4">
        {TAB_ITEMS.map(({ to, label, icon: Icon, end, prominent }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "relative flex items-center justify-center transition-colors",
                      prominent
                        ? "-mt-4 size-11 rounded-full bg-primary text-primary-foreground shadow-md"
                        : cn("h-7 w-12 rounded-full", isActive && "bg-accent")
                    )}
                  >
                    <Icon
                      className={cn(prominent ? "size-6" : "size-5")}
                      strokeWidth={isActive ? 2.4 : 1.8}
                    />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
